import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import TextInputWithSelectOnMap from '@/common/components/base/input/mapInput/TextInputWithSelectOnMap';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TimeInput from '@/common/components/base/input/timeInput/TimeInput';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import PercentageInput from '@/common/components/base/percentageInput/PercentageInput';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import MainLayout from '@/common/components/layouts/MainLayout';
import { useVisitList } from '@/common/hooks/useVisitList';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import convertTimestampToShamsi from '@/common/utils/convertTimestampToShamsi';
import { requiredText } from '@/common/utils/rules/publicRules';
import ImagesFabMainModal from '@/modules/adjusterReportModules/adjusterReport/components/imagesFab/ImagesFabMainModal';
import { validateNotPastDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { VisitInformationForm } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitInformation/getVisitInformationQuer.type';
import getVisitInformation from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitInformation/getVisitInformationQueryOption';
import { usePatchVisitInformationMutation } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchVisitInformation/patchVisitInformationQueryOption';
import { useQuery } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

export default () => {
  return (
    <MainLayout title="زیان‌دیده / کارشناسی" header="titleHeader">
      <VehicleVisitInformation />
    </MainLayout>
  );
};
const VehicleVisitInformation = () => {
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const { mutate: patchVisitformation, isPending: loading } =
    usePatchVisitInformationMutation();
  useQuery({
    ...getVisitInformation({
      damaged_id: damagedId!,
      id: id!,
      source: 'vehicle',
    }),
    meta: {
      onSuccess: (data) => {
        if (data) {
          methods.reset({
            ...data,
            visit_date: data?.visit_date
              ? convertTimestampToShamsi(data?.visit_date)
              : null,
            visit_site: {
              value: data.visit_site?.const,
              label: data.visit_site?.fa_const,
            },
            visit_location: data.visit_location,
            visit_location_phi: data.visit_location_phi ?? null,
            visit_location_lambda: data.visit_location_lambda ?? null,
          });
          if (data?.visit_date) {
            setTimeout(() => {
              methods.trigger('visit_date');
            });
          }
        }
      },
    },
  });
  const { visitListQuery } = useVisitList();
  const methods = useForm<VisitInformationForm>({
    mode: 'onChange',
  });

  const onSubmit = (formData: VisitInformationForm) => {
    const formattedData = {
      ...formData,
      visit_location: formData?.visit_location,
      visit_date: convertPersianToGregorian(
        formData?.visit_date?.toString() ?? '',
      ),
      visit_site: formData?.visit_site?.value,
      location_phi: formData?.visit_location_phi ?? null,
      location_lambda: formData?.visit_location_lambda ?? null,
    };
    patchVisitformation(
      {
        damaged_id: damagedId!,
        id: id!,
        data: formattedData,
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };

  return (
    <Box width="100%" pt={'24px'} position="relative">
      <TitleDescription>اطلاعات بازدید</TitleDescription>
      <FormProvider {...methods}>
        <Flex direction="column" gap={8} mt={24}>
          <Box>
            <DateInput
              label="تاریخ بازدید"
              name="visit_date"
              buttonText="انتخاب تاریخ"
              modalLabel="انتخاب تاریخ تولد"
              rules={{ required: requiredText, validate: validateNotPastDate }}
              minDate={new Date(Date.now() - 24 * 60 * 60 * 1000)}
            />
          </Box>
          <Box>
            <TimeInput
              buttonText="ثبت ساعت بازدید"
              modalLabel="انتخاب زمان بازدید"
              name="visit_time"
              label="ساعت بازدید"
              rules={{ required: requiredText }}
            />
          </Box>
          <Box>
            <PercentageInput
              name="visit_number"
              label="تعداد بازدید"
              percentage={1}
              maxPercentage={1000}
              placeHolder="تعداد بازدید را وارد کنید"
            />
          </Box>
          <Box>
            <ModalInput
              name="visit_site"
              label="محل بازدید"
              placeholder="انتخاب کنید"
              options={visitListQuery.data || []}
              defaultValue=""
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="مسافت طی شده (کیلومتر )"
              name="distance"
              placeholder="مسافت طی شده را وارد کنید"
              showPriceToWords={false}
              hasSeparator={false}
            />
          </Box>
          <Box>
            <PercentageInput
              name="parts_repair_length"
              label="طول تعمیر (روز)"
              percentage={1}
              maxPercentage={1000000}
              placeHolder="طول تعمیر را وارد کنید"
            />
          </Box>
          <Box>
            <TextInputWithSelectOnMap
              rules={{ required: requiredText }}
              label="آدرس محل بازدید"
              entranceModalText={
                'در ادامه آدرس محل وقوع حادثه را بر روی نقشه مشخص کنید .'
              }
              mapModalTitle="محل بازدید"
              name="visit_location"
              phiName="visit_location_phi"
              lambdaName="visit_location_lambda"
            />
          </Box>
          <Box pb={24}>
            <LoadingButton
              onClick={methods.handleSubmit(onSubmit)}
              loading={loading}
              fullWidth
              disabled={!methods?.formState?.isValid}
            >
              ثبت
            </LoadingButton>
          </Box>
        </Flex>
      </FormProvider>
      <ImagesFabMainModal state={'propertyDamaged'} />
    </Box>
  );
};
